import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { AUDIENCE_LIST } from "./audiences";
import { haptic } from "../../lib/haptics";

const SKIN = {
  noir: { wrap: "bg-[#0B0B0B] text-[#FAF7F2]", card: "bg-[#161616] border-[#C8A96B]/20", muted: "text-white/60", tabs: "bg-white/5 border-white/10", tabOff: "text-white/60 hover:text-white", feat: "bg-white/[0.03] border-white/10 hover:border-[#C8A96B]/50" },
  ivory: { wrap: "bg-[#F3EEE5] text-[#111]", card: "bg-white border-[#A38243]/25", muted: "text-[#665E52]", tabs: "bg-white/70 border-[#A38243]/20", tabOff: "text-[#665E52] hover:text-[#111]", feat: "bg-[#FAF7F2] border-[#A38243]/15 hover:border-[#A38243]/50" },
};

export default function AudienceSwitcher({ theme = "noir" }) {
  const [k, setK] = useState(0);
  const a = AUDIENCE_LIST[k];
  const sk = SKIN[theme] || SKIN.noir;

  return (
    <section id="audiences" data-testid="audience-switcher" className={`py-20 md:py-28 ${sk.wrap}`}>
      <div className="container-wide px-5 md:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end gap-6 justify-between mb-10">
          <div>
            <p className="font-script text-[var(--gold)] text-4xl">Built for everyone in love</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-1">One platform. Three ways in.</h2>
          </div>
          <div className={`inline-flex p-1 rounded-full border self-start lg:self-auto ${sk.tabs}`} role="tablist">
            {AUDIENCE_LIST.map((x, idx) => (
              <button key={x.key} role="tab" aria-selected={idx === k} data-testid={`audience-tab-${x.key}`}
                onClick={() => { setK(idx); haptic(); }}
                className={`relative px-5 md:px-7 py-2.5 rounded-full text-[11px] md:text-xs uppercase tracking-[0.2em] transition-colors ${idx === k ? "text-white" : sk.tabOff}`}>
                {idx === k && <motion.span layoutId="audience-pill" className="absolute inset-0 rounded-full bg-[var(--gold)]" transition={{ type: "spring", stiffness: 420, damping: 34 }} />}
                <span className="relative">{x.tab}</span>
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={a.key} data-testid="audience-content-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className={`grid lg:grid-cols-12 gap-6 rounded-[2rem] border p-4 md:p-6 ${sk.card}`}>
            <div className="lg:col-span-5 relative rounded-[1.5rem] overflow-hidden min-h-[280px] md:min-h-[420px] group">
              <img src={a.image} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
              <div className="absolute bottom-0 p-6 text-white">
                <p className="text-[10px] uppercase tracking-[0.28em] text-[var(--gold)]">{a.eyebrow}</p>
                <p className="font-serif text-3xl md:text-4xl leading-tight mt-2">{a.title} <span className="font-script text-[var(--gold)]">{a.accent}</span></p>
              </div>
            </div>
            <div className="lg:col-span-7 flex flex-col p-2 md:p-4 min-w-0">
              <p className={`text-base md:text-lg leading-relaxed ${sk.muted}`}>{a.blurb}</p>
              <div className="grid sm:grid-cols-2 gap-3 mt-6">
                {a.features.slice(0, 4).map((f, i) => (
                  <motion.div key={f.t} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i + 0.15 }}
                    className={`rounded-2xl border p-4 transition-colors duration-300 ${sk.feat}`}>
                    <f.icon className="w-5 h-5 text-[var(--gold)]" strokeWidth={1.6} />
                    <p className="font-serif text-lg mt-2">{f.t}</p>
                    <p className={`text-sm mt-0.5 ${sk.muted}`}>{f.d}</p>
                  </motion.div>
                ))}
              </div>
              <div className="mt-auto pt-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div data-testid="audience-price">
                  <p className="font-serif text-2xl flex items-center gap-2"><Check className="w-5 h-5 text-[var(--gold)]" /> {a.price.label}</p>
                  <p className={`text-xs uppercase tracking-[0.18em] mt-1 ${sk.muted}`}>{a.price.detail}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link to={a.cta.to} data-testid="audience-cta" className="btn-gold">{a.cta.label}</Link>
                  <Link to={a.path} data-testid="audience-explore-link" className="btn-outline-gold !text-inherit hover:!text-white">Explore <ArrowRight className="w-4 h-4" /></Link>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
